import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import _ from 'lodash';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNetBenefitData } from '../../hooks/use-net-benefit-data';
import { useRateValidation } from './use-rate-validation';

interface AdrValidatedRateFieldProps {
	name: string;
	label?: string;
	description?: string;
	min?: number;
	max?: number;
	step?: number;
	placeholder?: string;
	symbol?: {
		prefix?: string;
		suffix?: string;
	};
}

export function AdrValidatedRateField({
	name,
	label,
	description,
	min,
	max,
	step = 1,
	placeholder,
	symbol,
}: AdrValidatedRateFieldProps) {
	const form = useFormContext();
	const { rawCalculatedData } = useNetBenefitData();
	const fieldValue = useWatch({ control: form.control, name });

	// Use the rate validation hook for validation logic
	const { isRateTooLow, rateWarningMessage } = useRateValidation(fieldValue);

	// Calculate the default value based on average forecasted ADR if available
	const defaultValue = useMemo(() => {
		if (!rawCalculatedData || rawCalculatedData.length === 0) {
			return 100; // Default value if no data is available
		}

		// Calculate the average forecasted ADR
		const totalRevenue = rawCalculatedData.reduce(
			(sum, entry) => sum + (entry.fcst_rev || 0),
			0,
		);

		const totalSold = rawCalculatedData.reduce(
			(sum, entry) => sum + (entry.total_fcst || 0),
			0,
		);

		const avgForecastedADR = totalSold > 0 ? totalRevenue / totalSold : 0;

		// Return 50% of the average forecasted ADR, or 100 if it's not available
		return avgForecastedADR > 0 ? Math.ceil(avgForecastedADR * 0.5) : 100;
	}, [rawCalculatedData]);

	return (
		<div className='tw-space-y-2'>
			<FormField
				control={form.control}
				name={name}
				render={({ field }) => (
					<FormItem>
						<FormLabel>
							{label ?? _.startCase(name)}{' '}
							{(symbol?.prefix || symbol?.suffix) && (
								<span className='tw-text-muted-foreground tw-ml-2'>
									{symbol?.prefix}
									{field.value || defaultValue}
									{symbol?.suffix}
								</span>
							)}
						</FormLabel>
						<FormControl>
							<div className='tw-flex tw-items-center tw-space-x-2'>
								{symbol?.prefix && (
									<span className='tw-text-sm'>{symbol.prefix}</span>
								)}
								<Input
									className={`tw-bg-white hover:tw-bg-secondary ${isRateTooLow ? 'tw-border-red-500' : ''}`}
									type='number'
									min={min}
									max={max}
									step={step}
									placeholder={placeholder}
									{...field}
									onChange={(e) => {
										const value =
											e.target.value === '' ? 0 : Number(e.target.value);
										field.onChange(value);
									}}
								/>
								{symbol?.suffix && (
									<span className='tw-text-sm'>{symbol.suffix}</span>
								)}
							</div>
						</FormControl>
						<FormDescription>{description}</FormDescription>
						<FormMessage />
						{isRateTooLow && (
							<p className='tw-text-red-500 tw-text-sm tw-mt-1'>
								{rateWarningMessage}
							</p>
						)}
					</FormItem>
				)}
			/>
		</div>
	);
}
